import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Stack } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useAssignUserCoupon } from '../../hooks/coupons/mutation/V2';
import { IAuth } from '../../ts/interface/admins.interface';
import { findAccess } from '../../utils';
import { useAdminStore } from '../../store/adminStore';
import { useSystemStore } from '../../store/systemStore';
import useOrganization from '../../hooks/use-organization';
import IconButtonMenu from '../Redesing/icon-button-menu';
import MovaticCustomModal from '../Modal/MovaticCustomModal';
import CustomAsyncToast from '../Redesing/custom-async-toast';
import UserSearch, { type SelectedUser } from './UserSearch';

interface AssignCouponToUserProps {
  scope: 'system' | 'organization';
  /** Whether the assign dialog is open. */
  open: boolean;
  /** Close the dialog (Cancel / backdrop). */
  onClose: () => void;
  /** Called after a successful assignment (parent closes + refetches). */
  onAssigned?: () => void;
  /** The coupon's legacy `oldId` to assign. */
  couponOldId: string;
  /** Display name/code for the dialog title. */
  couponName?: string;
  systemAccess: number;
  movaticAccess: number;
}

const AssignCouponToUser = ({
  scope,
  open,
  onClose,
  onAssigned,
  couponOldId,
  couponName,
  systemAccess,
  movaticAccess,
}: AssignCouponToUserProps) => {
  const organization = useOrganization();
  const adminId = useAdminStore((state) => state.admin.admin?.id) ?? '';
  // The user-search filter scopes by userSystems.systemId, which is the system
  // GID (UUID) — not the legacy systemOldId. Use the current system's GID.
  const systemGid = useSystemStore((state) => state.systemInfo.gid);

  const isOrg = scope === 'organization';
  const accessLevel = isOrg ? organization?.access ?? systemAccess : systemAccess;
  const canAssign = findAccess(accessLevel).includes('rates') || Boolean(movaticAccess);

  const [selectedUser, setSelectedUser] = useState<SelectedUser | null>(null);
  const { assignUserCoupon, isLoading: isAssigning } = useAssignUserCoupon();

  const submitDisabled = !canAssign || !selectedUser || !couponOldId || isAssigning;

  const closeModal = () => {
    setSelectedUser(null);
    onClose();
  };

  const handleAssign = () => {
    if (!selectedUser || !couponOldId) return;

    CustomAsyncToast({
      loadingMessage: 'Assigning coupon...',
      successMessage: () => 'Coupon assigned successfully!',
      errorMessage: 'There was an error assigning the coupon.',
      promise: () =>
        assignUserCoupon({
          couponOldId,
          userOldId: selectedUser.oldId,
          userId: selectedUser.id,
          assignedBy: adminId || null,
        }).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors[0]?.message || 'Failed to assign the coupon.');
          }
          return true;
        }),
    }).then((result) => {
      if (result) {
        setSelectedUser(null);
        onAssigned?.();
      }
    });
  };

  return (
    <MovaticCustomModal
      useContentDividers
      open={open}
      onClose={closeModal}
      isLoading={isAssigning}
      title={`Assign ${couponName || 'Coupon'}`}
      content={
        <Stack spacing={2} sx={{ minWidth: 320 }}>
          <UserSearch
            description={'Search for a user by name or phone to grant this coupon.'}
            systemId={systemGid || undefined}
            organizationId={systemGid ? undefined : organization?.id || undefined}
            selectedUser={
              selectedUser ? { value: selectedUser.id, label: selectedUser.label } : null
            }
            onChange={setSelectedUser}
          />
        </Stack>
      }
      action={
        <IconButtonMenu
          buttons={[
            {
              label: 'Cancel',
              dataId: 'coupon-assign-cancel',
              onClick: closeModal,
              startIcon: <CloseIcon />,
            },
            {
              label: 'Assign',
              dataId: 'coupon-assign-submit',
              disabled: submitDisabled,
              onClick: handleAssign,
              startIcon: <CheckCircleIcon />,
            },
          ]}
        />
      }
    />
  );
};

const mapState = (state: {
  system: { isLoaded: boolean };
  admin: { admin: IAuth; systemAccess: number };
}) => ({
  systemAccess: state.admin.systemAccess,
  movaticAccess: state.admin.admin.admin.movaticAccess,
});

export const AssignCouponToUserSystem = connect(mapState)(
  (props: Omit<AssignCouponToUserProps, 'scope'>) => (
    <AssignCouponToUser {...props} scope="system" />
  )
);

export const AssignCouponToUserOrganization = connect(mapState)(
  (props: Omit<AssignCouponToUserProps, 'scope'>) => (
    <AssignCouponToUser {...props} scope="organization" />
  )
);

export default AssignCouponToUser;
